import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AdminPlanUpgradeRequest, PlanUpgradeRequestStatus } from '@terafile/shared'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { FETCH_ERROR_FALLBACK, SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { LOADING } from '../lib/labels.js'
import {
  listAdminPlanUpgradeRequests,
  updateAdminPlanUpgradeRequest,
} from '../lib/plan-upgrade-api.js'
import { useToast } from '../lib/toast.js'

const STATUS_OPTIONS: Array<{ value: '' | PlanUpgradeRequestStatus; label: string }> = [
  { value: '', label: 'すべて' },
  { value: 'PENDING', label: '未対応' },
  { value: 'CONTACTED', label: '対応中' },
  { value: 'APPROVED', label: '承認' },
  { value: 'REJECTED', label: '見送り' },
  { value: 'CANCELLED', label: 'キャンセル' },
]

const STATUS_LABEL: Record<PlanUpgradeRequestStatus, { label: string; className: string }> = {
  PENDING: { label: '未対応', className: 'border-amber-300 text-amber-900 bg-amber-50' },
  CONTACTED: { label: '対応中', className: 'border-sky-300 text-sky-900 bg-sky-50' },
  APPROVED: { label: '承認', className: 'border-emerald-300 text-emerald-900 bg-emerald-50' },
  REJECTED: {
    label: '見送り',
    className: 'border-destructive/30 text-destructive bg-destructive/5',
  },
  CANCELLED: { label: 'キャンセル', className: 'border-border text-muted-foreground bg-muted/30' },
}

export function AdminPlanUpgradeRequestsPage() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [statusFilter, setStatusFilter] = useState<'' | PlanUpgradeRequestStatus>('PENDING')

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-plan-upgrade-requests', statusFilter],
    queryFn: () =>
      listAdminPlanUpgradeRequests({
        ...(statusFilter ? { status: statusFilter } : {}),
        limit: 100,
      }),
  })

  const updateMut = useMutation({
    mutationFn: ({
      id,
      status,
      note,
    }: {
      id: string
      status: PlanUpgradeRequestStatus
      note?: string | undefined
    }) =>
      updateAdminPlanUpgradeRequest(id, {
        status,
        ...(note !== undefined ? { reviewerNote: note } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plan-upgrade-requests'] })
      queryClient.invalidateQueries({ queryKey: ['admin-dashboard-summary'] })
      toast.success('ステータスを更新しました')
    },
    onError: (err) => {
      toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-16 space-y-8 grain">
      <section>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground/80 mb-3">
          Operations · Plan Upgrade
        </p>
        <h1 className="font-display text-4xl md:text-5xl font-semibold leading-[1.05] tracking-tight">
          有料プラン申込み
        </h1>
        {data && (
          <p className="mt-4 text-sm text-muted-foreground">
            全{' '}
            <span className="font-medium text-foreground editorial-num">
              {data.total.toLocaleString('ja-JP')}
            </span>{' '}
            件 · 未対応{' '}
            <span className="font-medium text-amber-700 editorial-num">
              {data.pendingCount.toLocaleString('ja-JP')}
            </span>{' '}
            件
          </p>
        )}
      </section>

      <div className="flex items-center gap-3 border-b border-border pb-3">
        <label
          htmlFor="status"
          className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-mono"
        >
          ステータス
        </label>
        <select
          id="status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          className="px-3 py-1.5 border border-border rounded-md text-sm bg-background"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>}
      {error && (
        <div className="border border-destructive/40 bg-destructive/5 rounded-md p-4 text-sm text-destructive">
          {toUserMessage(error, FETCH_ERROR_FALLBACK)}
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="border border-dashed border-border rounded-lg py-12 text-center bg-muted/20">
          <p className="text-sm text-muted-foreground">該当する申請はありません</p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <ul className="space-y-3">
          {data.items.map((r) => (
            <RequestCard
              key={r.id}
              request={r}
              onUpdate={(status, note) =>
                updateMut.mutate({ id: r.id, status, note: note ?? undefined })
              }
              pending={updateMut.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function RequestCard({
  request,
  onUpdate,
  pending,
}: {
  request: AdminPlanUpgradeRequest
  onUpdate: (status: PlanUpgradeRequestStatus, note: string | null) => void
  pending: boolean
}) {
  const [note, setNote] = useState(request.reviewerNote ?? '')
  const conf = STATUS_LABEL[request.status]

  return (
    <li className="border border-border rounded-lg p-5 bg-card space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-full font-medium ${conf.className}`}
            >
              {conf.label}
            </span>
            <h2 className="font-display text-lg font-medium tracking-tight truncate">
              {request.companyName ?? request.contactName ?? request.contactEmail}
            </h2>
          </div>
          <p className="mt-1 text-xs text-muted-foreground font-mono">
            {new Date(request.createdAt).toLocaleString('ja-JP')} · ID {request.id.slice(0, 8)}
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <Item label="ワークスペース">
          {request.tenant ? (
            <>
              <Link
                to={`/admin/tenants/${request.tenant.id}`}
                className="hover:underline underline-offset-4"
              >
                {request.tenant.name ?? '(無名)'}
              </Link>
              <span className="ml-2 text-xs text-muted-foreground">({request.tenant.plan})</span>
            </>
          ) : (
            <span className="text-muted-foreground italic">(削除済み)</span>
          )}
        </Item>
        <Item label="申請者">
          {request.contactName ?? '(未入力)'} · {request.contactEmail}
        </Item>
        <Item label="電話">{request.phone ?? '(未入力)'}</Item>
        <Item label="希望追加ストレージ">+{request.desiredAddonStorageTb} TB</Item>
      </dl>

      {/* 請求情報セクション。発行担当が一目で住所と送付先メアドを把握できるよう独立 */}
      <div className="border-t border-border pt-3 space-y-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80 font-mono">
          請求情報
        </p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Item label="請求書送付先メールアドレス">
            {request.billingEmail ?? (
              <span className="text-muted-foreground italic">
                (申請者と同じ: {request.contactEmail})
              </span>
            )}
          </Item>
          <Item label="請求先住所">
            {request.postalCode || request.billingAddress ? (
              <span className="break-all">
                {request.postalCode && <>〒{request.postalCode} </>}
                {request.billingAddress ?? ''}
              </span>
            ) : (
              <span className="text-muted-foreground italic">(未入力)</span>
            )}
          </Item>
        </dl>
      </div>

      {request.message && (
        <div className="text-sm border-l-2 border-border pl-3 text-muted-foreground whitespace-pre-wrap">
          {request.message}
        </div>
      )}

      <div className="space-y-2 pt-2 border-t border-border">
        <label className="block">
          <span className="text-xs text-muted-foreground">管理者メモ</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={2000}
            className="w-full mt-1 px-3 py-2 border border-border rounded-md text-sm bg-background"
            placeholder="対応経過・連絡先・却下理由など"
          />
        </label>
        <div className="flex items-center gap-2 flex-wrap">
          {(['CONTACTED', 'APPROVED', 'REJECTED'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onUpdate(s, note || null)}
              disabled={pending || request.status === s}
              className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-accent transition-colors disabled:opacity-40"
            >
              {STATUS_LABEL[s].label}にする
            </button>
          ))}
          {request.tenant && request.tenant.plan !== 'PRO' && request.status === 'APPROVED' && (
            <Link
              to={`/admin/tenants/${request.tenant.id}`}
              className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity"
            >
              テナント詳細でプラン変更 →
            </Link>
          )}
        </div>
      </div>
    </li>
  )
}

function Item({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  )
}
